import React, { useEffect, useState } from "react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { getDashboard } from "../api/dashboardService";
import { Badge } from "../components/common";

export default function Dashboard({ role, onOpen }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const catColors = ["#0e2a47", "#dc9a34", "#1f7a5c", "#a83a3a"];

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-600">
        Loading Dashboard...
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load dashboard.
      </div>
    );
  }

  const stats = [
    ["एकूण शिक्षक", dashboard.stats.totalTeachers, "#0e2a47"],
    ["सादर झालेले अर्ज", dashboard.stats.submittedApplications, "#1f5e9c"],
    ["पडताळणी पूर्ण", dashboard.stats.underVerification, "#1f7a5c"],
    ["मंजूर अर्ज", dashboard.stats.approvedApplications, "#227a3e"],
  ];

  const catData = dashboard.categoryDistribution.map((x) => ({
    name: x.name,
    value: x.count,
  }));

  const teacherRows = dashboard.recentTeachers;

  return (
    <div className="p-4 sm:p-7">
      {/* Header */}

      <div className="mb-5">
        <h1
          className="text-2xl font-normal mb-1"
          style={{ fontFamily: "serif" }}
        >
          नमस्कार, {role}
        </h1>

        <p className="text-sm text-slate-500">
          सन २०२६-२०२७ साठी सद्यस्थिती एका दृष्टीक्षेपात
        </p>
      </div>

      {/* Statistics */}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5 mb-6">
        {stats.map(([label, num, color]) => (
          <div
            key={label}
            className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3.5"
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
              style={{
                background: color + "22",
                color,
                border: `2px solid ${color}55`,
              }}
            >
              {num}
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-500 mb-0.5">
                {label}
              </div>

              <div className="text-xl font-bold">{num}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Teachers */}

      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 mb-5">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 mb-3">
          <h3 className="text-sm font-bold text-slate-800">शिक्षक यादी</h3>

          <span className="text-[11px] text-slate-400">
            अलीकडील {teacherRows.length} नोंदी
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[760px]">
            <thead>
              <tr className="text-slate-500 border-b border-slate-200">
                {[
                  "शिक्षकाचे नाव",
                  "मोबाईल नंबर",
                  "शाळा",
                  "तालुका",
                  "पदनाम",
                  "स्थिती",
                  "गुण",
                  "",
                ].map((h) => (
                  <th key={h} className="text-left px-2.5 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {teacherRows.map((a) => (
                <tr
                  key={a.teacherId}
                  className="border-b border-slate-100 hover:bg-amber-50"
                >
                  <td className="px-2.5 py-2 font-medium whitespace-nowrap">
                    {a.name}
                  </td>

                  <td className="px-2.5 py-2 font-mono whitespace-nowrap">
                    {a.mobile}
                  </td>

                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {a.school}
                  </td>

                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {a.taluka}
                  </td>

                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {a.category}
                  </td>

                  <td className="px-2.5 py-2">
                    <Badge status={a.status} />
                  </td>

                  <td className="px-2.5 py-2 font-mono whitespace-nowrap">
                    {a.score ?? "-"}
                  </td>

                  <td className="px-2.5 py-2">
                    {/* <button
                      onClick={() => onOpen(a.teacherId)}
                      className="text-[11px] font-semibold text-blue-600 hover:underline"
                    >
                      पहा
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-slate-800 mb-3">
            पदनामनिहाय वितरण
          </h3>

          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={catData}
                dataKey="value"
                nameKey="name"
                outerRadius={75}
                label={{ fontSize: 10 }}
              >
                {catData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={catColors[i % catColors.length]}
                  />
                ))}
              </Pie>

              <Legend wrapperStyle={{ fontSize: 10 }} />

              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Activities */}

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-slate-800 mb-3">
            अलीकडील हालचाली
          </h3>

          {dashboard.recentActivities.map((a) => (
            <div
              key={a.teacherId}
              className="flex gap-2.5 py-2 border-b border-dashed border-slate-200 last:border-0 text-xs"
            >
              <div
                className="w-2 h-2 rounded-full mt-1 shrink-0"
                style={{ background: "#dc9a34" }}
              />

              <div>
                <b className="font-semibold">
                  {a.teacherName}
                </b>{" "}
                — {a.status}

                <small className="block text-slate-400">
                  {a.applicationNumber} · {a.taluka} तालुका ·{" "}
                  {new Date(a.updatedOn).toLocaleDateString("mr-IN")}
                </small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}